import { useEffect, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Minus,
  Loader2,
  X,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { cn } from '@/lib/cn';
import { toast } from '@/store/toastStore';
import { extractYouTubeUrls } from '@/lib/youtube';
import { YouTubePreviewList } from '@/components/molecules/YouTubePreview';

/**
 * 게시글/매매일지용 리치 텍스트 에디터 (Tiptap v3)
 *
 * 보안 원칙:
 * - 출력 HTML 은 반드시 백엔드 sanitize-html 을 통과해야 저장됨
 * - 이미지 업로드는 apiClient (/upload/image) 로만 — 외부 URL 삽입 UI 제공하지 않음
 * - 링크는 http/https 만 받고 tiptap Link 확장이 rel=noopener noreferrer / target=_blank 강제
 */

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxLength?: number;
  /** 업로드 중 로딩 외부 통지 (버튼 disable 등) */
  onUploadingChange?: (uploading: boolean) => void;
}

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
/** 본문에 첨부 가능한 최대 이미지 수 (백엔드 imageUrls.max(5) 와 동일) */
const MAX_IMAGES = 5;

/** 에디터 문서에서 현재 이미지 src 목록 추출 */
function collectImageSrcs(editor: Editor | null): string[] {
  if (!editor) return [];
  const srcs: string[] = [];
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'image') {
      const src = node.attrs.src as string | undefined;
      if (src) srcs.push(src);
    }
  });
  return srcs;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = '내용을 입력하세요',
  maxLength,
  onUploadingChange,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  /* 첨부 이미지 미리보기 목록 — 문서 내 <img> src 를 추적해 썸네일 스트립 + 개별 삭제 제공 */
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // StarterKit 내부 Link 는 사용하지 않음 (별도 Link 확장으로 덮어씀)
        link: false as any,
        // 보안: 코드블록/수식 등 불필요한 확장은 기본값 유지
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        // http/https 만 허용
        protocols: ['http', 'https'],
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      Image.configure({
        // src 속성에 사용자 입력 URL 을 그대로 받지 않도록 inline=false, allowBase64=false
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'rte-image',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        // min-h-full: 빈 에디터도 스크롤 컨테이너를 가득 채워 클릭 영역 확보.
        // 높이 제한은 바깥 스크롤 컨테이너가 담당 (내부 스크롤).
        class:
          'prose prose-sm max-w-none min-h-full px-4 py-3 focus:outline-none',
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      if (maxLength && html.length > maxLength) {
        // 초과분은 저장만 막고 입력은 그대로 (UX 상 블락하지 않음) — 저장 시 canSubmit 로 방어
      }
      onChange(html);
      setImageSrcs(collectImageSrcs(e));
    },
  });

  // 외부 value 변경(edit 모드 프리로드) → 에디터 내용 동기화
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // 프리로드/외부 변경 후 이미지 목록 동기화
    setImageSrcs(collectImageSrcs(editor));
  }, [value, editor]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  /* 다중 이미지 업로드 — 한 번에 여러 장 선택 가능, 본문 내 총 이미지 최대 5장.
     순차 업로드 후 각 이미지를 본문에 인라인 삽입. */
  const uploadImages = useCallback(
    async (files: File[]) => {
      if (!editor || files.length === 0) return;
      const already = collectImageSrcs(editor).length;
      const slot = MAX_IMAGES - already;
      if (slot <= 0) {
        toast.error(`이미지는 최대 ${MAX_IMAGES}장까지 첨부할 수 있어요.`);
        return;
      }
      let targets = files;
      if (files.length > slot) {
        toast.error(`이미지는 최대 ${MAX_IMAGES}장까지예요. ${slot}장만 첨부됩니다.`);
        targets = files.slice(0, slot);
      }

      setUploading(true);
      try {
        for (const file of targets) {
          if (!ALLOWED_MIME.includes(file.type)) {
            toast.error(`${file.name}: JPG/PNG/WebP 형식만 업로드할 수 있어요.`);
            continue;
          }
          if (file.size > MAX_IMAGE_SIZE) {
            toast.error(`${file.name}: 5MB 이하 이미지만 업로드할 수 있어요.`);
            continue;
          }
          try {
            const fd = new FormData();
            fd.append('image', file);
            const res = await apiClient.post('/upload/image', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const url = res.data?.data?.url as string | undefined;
            if (!url || !url.startsWith('/uploads/')) {
              toast.error('업로드 응답이 올바르지 않아요.');
              continue;
            }
            /* setImage 는 현재 선택(직전 삽입 이미지의 NodeSelection)을 교체해버려
               여러 장 삽입 시 마지막 1장만 남는 버그가 있음.
               selection.to(현재 커서/노드 끝) 위치에 명시적으로 삽입해 기존 이미지를 보존한다.
               insertContentAt 은 삽입 후 커서를 콘텐츠 뒤로 옮겨, 다음 이미지는 이어서 삽입됨. */
            const insertPos = editor.state.selection.to;
            editor
              .chain()
              .focus()
              .insertContentAt(insertPos, { type: 'image', attrs: { src: url, alt: '' } })
              .run();
          } catch (err: any) {
            toast.error(err?.response?.data?.error?.message ?? `${file.name} 업로드 실패`);
          }
        }
        setImageSrcs(collectImageSrcs(editor));
      } finally {
        setUploading(false);
      }
    },
    [editor],
  );

  /* 특정 이미지(src) 를 본문에서 제거 — 썸네일 스트립의 ✕ 버튼용 */
  const removeImage = useCallback(
    (src: string) => {
      if (!editor) return;
      let from = -1;
      let to = -1;
      editor.state.doc.descendants((node, pos) => {
        if (from >= 0) return false;
        if (node.type.name === 'image' && node.attrs.src === src) {
          from = pos;
          to = pos + node.nodeSize;
          return false;
        }
        return true;
      });
      if (from >= 0) {
        editor.chain().focus().deleteRange({ from, to }).run();
        setImageSrcs(collectImageSrcs(editor));
      }
    },
    [editor],
  );

  const pickImage = () => fileRef.current?.click();

  /* 본문에 입력/붙여넣기된 YouTube 링크 감지 → 미리보기 카드 (상세 페이지와 동일 표시) */
  const youtubeUrls = extractYouTubeUrls(value);

  const addLink = () => {
    if (!editor) return;
    const current = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('링크 URL (http/https):', current ?? 'https://');
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      toast.error('http/https 로 시작하는 URL 만 입력할 수 있어요.');
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: trimmed })
      .run();
  };

  if (!editor) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-sub)]">
        에디터 로딩 중…
      </div>
    );
  }

  return (
    <div>
    <div className="flex flex-col overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
      {/* 툴바 — 에디터 상단 고정 (이미지 첨부 버튼 항상 노출) */}
      <div className="shrink-0 border-b border-[var(--color-border)]">
        <Toolbar
          editor={editor}
          onAddLink={addLink}
          onPickImage={pickImage}
          uploading={uploading}
          imageCount={imageSrcs.length}
        />
      </div>

      {/* 본문 — 높이 고정 + 내부 스크롤 (모바일 60vh, 데스크톱 최대 460px) */}
      <div className="h-[60vh] max-h-[460px] min-h-[280px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      {/* 첨부 이미지 썸네일 스트립 — 미리보기 + 개별 삭제 (최대 5장) */}
      {imageSrcs.length > 0 && (
        <div className="shrink-0 border-t border-[var(--color-border)] p-2">
          <div className="mb-1 px-1 text-[11px] text-[var(--color-text-sub)]">
            첨부 이미지 {imageSrcs.length}/{MAX_IMAGES}
          </div>
          <div className="flex flex-wrap gap-2">
            {imageSrcs.map((src, i) => (
              <div
                key={`${src}-${i}`}
                className="relative h-16 w-16 overflow-hidden rounded-md border border-[var(--color-border)]"
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  title="이미지 삭제"
                  onClick={() => removeImage(src)}
                  className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          if (files.length > 0) uploadImages(files);
          e.target.value = '';
        }}
      />
    </div>

      {/* YouTube 미리보기 — 본문 링크 자동 감지 */}
      {youtubeUrls.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 text-[11px] text-[var(--color-text-sub)]">
            YouTube 미리보기
          </div>
          <YouTubePreviewList urls={youtubeUrls} />
        </div>
      )}
    </div>
  );
}

interface ToolbarProps {
  editor: Editor;
  onAddLink: () => void;
  onPickImage: () => void;
  uploading: boolean;
  imageCount: number;
}

function Toolbar({ editor, onAddLink, onPickImage, uploading, imageCount }: ToolbarProps) {
  const imageFull = imageCount >= MAX_IMAGES;
  /* 버튼은 기본 inline-flex. 모바일(< sm) 에서 숨길 항목은 `hidden sm:inline-flex`,
     구분선은 `hidden sm:inline-block` 으로 자연스럽게 사라지도록 한다.
     이미지 첨부 버튼만 모바일에서도 노출 — 사용자 요청. */
  const btn = (active: boolean, mobileVisible = false) =>
    cn(
      'h-8 w-8 items-center justify-center rounded-md transition-colors',
      mobileVisible ? 'inline-flex' : 'hidden sm:inline-flex',
      active
        ? 'bg-[#62b058] text-white'
        : 'text-[var(--color-text)] hover:bg-gray-100',
    );

  return (
    <div className="flex flex-wrap items-center gap-1 p-2">
      <button
        type="button"
        title="굵게"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btn(editor.isActive('bold'))}
      >
        <Bold className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="기울임"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btn(editor.isActive('italic'))}
      >
        <Italic className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="밑줄"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={btn(editor.isActive('underline'))}
      >
        <UnderlineIcon className="h-4 w-4" />
      </button>

      <span className="mx-1 hidden h-5 w-px bg-[var(--color-border)] sm:inline-block" />

      <button
        type="button"
        title="H2"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={btn(editor.isActive('heading', { level: 2 }))}
      >
        <Heading2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="H3"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={btn(editor.isActive('heading', { level: 3 }))}
      >
        <Heading3 className="h-4 w-4" />
      </button>

      <span className="mx-1 hidden h-5 w-px bg-[var(--color-border)] sm:inline-block" />

      <button
        type="button"
        title="글머리 기호"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btn(editor.isActive('bulletList'))}
      >
        <List className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="번호 매기기"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btn(editor.isActive('orderedList'))}
      >
        <ListOrdered className="h-4 w-4" />
      </button>

      <span className="mx-1 hidden h-5 w-px bg-[var(--color-border)] sm:inline-block" />

      <button
        type="button"
        title="링크"
        onClick={onAddLink}
        className={btn(editor.isActive('link'))}
      >
        <LinkIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        title={imageFull ? `이미지는 최대 ${MAX_IMAGES}장` : '이미지 첨부'}
        onClick={onPickImage}
        disabled={uploading || imageFull}
        className={cn(
          btn(false, true),
          (uploading || imageFull) && 'cursor-not-allowed opacity-50',
        )}
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ImageIcon className="h-4 w-4" />
        )}
      </button>
      <button
        type="button"
        title="구분선"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className={btn(false)}
      >
        <Minus className="h-4 w-4" />
      </button>
    </div>
  );
}
